import { SupabaseError, shouldRethrow } from './api/shared'
import { soundsApi } from './supabase'
import { supabase } from './supabase-client'
import type { Sound } from './supabase-types'
import { EXCLUDE_MATURE_FILTER } from './mature-content'

export interface Vibe {
  slug: string
  name: string
  /**
   * Accent as a `var(--vibe-<slug>)` reference, consumed inline via
   * `color`/`borderColor`. The eight-hue vibe palette is RETIRED (owner
   * ruling, 2026-08-14, following the category-rainbow retirement): every
   * `--vibe-*` token in app/globals.css now resolves to the single blue
   * accent (`--tls-brand`), per-theme for AA contrast. The per-vibe field
   * is kept so call sites keep working; hue no longer varies by vibe.
   */
  accent: string
  /** Categories from the existing taxonomy that match this vibe. */
  categories: string[]
  /** Hero lede paragraph for the landing page. */
  lede: string
  /** Short OG/meta description (≤155 chars). */
  metaDescription: string
  /**
   * Answer to "what makes a lock sound feel <vibe>?" — a concrete, sensory
   * description for the chooser, NOT a description of how the list was built.
   * Drives the lead FAQ answer on /sounds/vibe/[slug] so each page is distinct.
   */
  faqLede: string
}

// Vibe taxonomy is intentionally small (8 entries) and content-aligned with
// the existing 15 categories so we can derive the picks without any schema
// changes or AI tagging passes — purely a discovery overlay.
export const VIBES: Vibe[] = [
  {
    slug: 'comedy',
    name: 'Comedy',
    accent: 'var(--vibe-comedy)',
    categories: ['Classic Memes', 'Sound Effects', 'Cartoons & Animation'],
    lede: 'Lock chimes that make the group chat laugh. Hand-picked from the meme, sound-effect, and cartoon catalog: every file is a Tesla-spec WAV.',
    metaDescription:
      'Funny Tesla lock sounds: top picks from memes, comedy effects, and cartoon classics. Free WAV downloads.',
    faqLede:
      'A comedy chime lands in under a second and breaks the moment a car normally just beeps: a Vine boom, a record scratch, a cartoon spring. Reach for one when you want a double-take in the parking lot.',
  },
  {
    slug: 'wholesome',
    name: 'Wholesome',
    accent: 'var(--vibe-wholesome)',
    categories: ['Cartoons & Animation', 'Nature & Animals', 'Music'],
    lede: 'Lock sounds your kids and your in-laws will both like. Gentle, family-safe picks across animation, nature, and warm musical chimes.',
    metaDescription:
      'Wholesome Tesla lock sounds: gentle, family-friendly WAVs from animation, nature, and warm musical picks.',
    faqLede:
      'A wholesome chime is soft and friendly: a cartoon twinkle, a birdsong, a warm few notes of melody. Nothing startling, nothing crude. Reach for one when the kids are in the car and you still want something that is not the stock beep.',
  },
  {
    slug: 'cursed',
    name: 'Cursed',
    accent: 'var(--vibe-cursed)',
    categories: ['Classic Memes', 'Sound Effects', 'Horror & Spooky'],
    lede: 'Lock chimes that should not be. Brainrot, surreal meme noise, and just-a-bit-too-loud horror cuts, for the Tesla owner who treats Boombox as a personal joke.',
    metaDescription:
      'Cursed Tesla lock sounds: surreal meme noise and unsettling picks. Free WAVs, Boombox-ready.',
    faqLede:
      'A cursed chime is wrong on purpose: brainrot noise, a distorted meme, a horror cut that is a little too loud for a driveway. Reach for one when you want the people nearby to slowly turn and look.',
  },
  {
    slug: 'minimalist',
    name: 'Minimalist',
    accent: 'var(--vibe-minimalist)',
    categories: ['Sound Effects', 'Tesla & EV', 'Retro Tech'],
    lede: 'Restrained lock chimes that signal without shouting. Clean tones, soft electronic textures, and stock-Tesla-adjacent picks for owners who want subtle.',
    metaDescription:
      'Minimalist Tesla lock sounds: clean, quiet, restrained WAVs. Free downloads, Tesla-spec format.',
    faqLede:
      'A minimalist chime is a single clean tone: a soft electronic blip, a short marimba tap, something you would not mind hearing ten times a day. Reach for one when you want custom that still reads as stock-Tesla restraint.',
  },
  {
    slug: 'nostalgic',
    name: 'Nostalgic',
    accent: 'var(--vibe-nostalgic)',
    categories: ['Modern Gaming', 'Retro Tech', 'Movies'],
    lede: 'Lock chimes that feel like home. Modern gaming sounds, classic computer beeps, and movie themes: Tesla as time machine.',
    metaDescription:
      'Nostalgic Tesla lock sounds: modern gaming, classic tech, and movie picks. Free WAVs.',
    faqLede:
      'A nostalgic chime pulls a specific memory: an 8-bit coin grab, a dial-up handshake, the first three notes of a TV theme. Reach for one when you want the lock to feel like a time machine to whoever recognizes it.',
  },
  {
    slug: 'aggressive',
    name: 'Aggressive',
    accent: 'var(--vibe-aggressive)',
    categories: ['Sports', 'Sci-Fi & Futuristic', 'Modern Gaming'],
    lede: 'Lock chimes with edge. Stadium horns, sci-fi impacts, and modern-gaming combat cues, for the Tesla that arrives loudly.',
    metaDescription:
      'Aggressive Tesla lock sounds: stadium horns, sci-fi hits, and modern-gaming impacts. Free WAVs.',
    faqLede:
      'An aggressive chime hits hard and fast: a stadium air-horn, a heavy sci-fi impact, a combat cue from a shooter. Reach for one when you want the car to announce itself the second it locks.',
  },
  {
    slug: 'mysterious',
    name: 'Mysterious',
    accent: 'var(--vibe-mysterious)',
    categories: ['Horror & Spooky', 'Sci-Fi & Futuristic', 'Movies'],
    lede: 'Lock chimes that hint at something. Atmospheric horror cues, sci-fi whispers, and detective-show signature notes: Tesla as a slow reveal.',
    metaDescription:
      'Mysterious Tesla lock sounds: atmospheric, eerie, sci-fi picks. Free WAVs, Boombox-ready.',
    faqLede:
      'A mysterious chime hints rather than announces: a low atmospheric swell, a single eerie sci-fi tone, the signature sting from a detective show. Reach for one when you want the driveway to feel like the cold open of an episode.',
  },
  {
    slug: 'cinematic',
    name: 'Cinematic',
    accent: 'var(--vibe-cinematic)',
    categories: ['Movies', 'Sci-Fi & Futuristic', 'Music'],
    lede: 'Lock chimes that score the moment. Movie cues, blockbuster sci-fi stings, and orchestral music: Tesla locks like a trailer cuts.',
    metaDescription:
      'Cinematic Tesla lock sounds: movie cues, sci-fi stings, orchestral music. Free WAVs.',
    faqLede:
      'A cinematic chime scores the moment: a blockbuster sci-fi sting, a few bars of orchestral swell, a recognizable movie cue. Reach for one when you want locking the car to land like the cut at the end of a trailer.',
  },
]

export function getVibeBySlug(slug: string): Vibe | undefined {
  return VIBES.find((v) => v.slug === slug)
}

/**
 * Fetches the top sounds for a vibe by unioning across the vibe's categories,
 * deduplicating by ID, and sorting by download count descending.
 *
 * No DB schema changes required — derived from the existing 15-category
 * taxonomy. Suitable for SSG pages with `revalidate`.
 */
/**
 * Resolves the picks for a vibe page.
 *
 * Strategy (in priority order):
 *   1. Direct DB query on `vibe_tags @> ARRAY[slug]` — uses the
 *      sounds_vibe_tags_gin index from migration 20260520.
 *      Populated for 95.5% of active sounds by the SQL backfill.
 *   2. Category-derived fallback for any sounds without vibe_tags yet
 *      (legacy/edge cases). Unions the vibe's categories.
 *
 * Both paths return sounds sorted by downloads desc.
 *
 * ISR callers pass `{ throwOnError: true }` so a transient Supabase failure
 * rejects the render instead of caching an empty vibe hub for the route's
 * revalidate window. A *successful* query that returns no rows is not a
 * failure — it still falls through to the category union.
 */
export async function getSoundsByVibe(
  slug: string,
  limit = 24,
  options: { throwOnError?: boolean } = {},
): Promise<Sound[]> {
  const vibe = getVibeBySlug(slug)
  if (!vibe) return []

  if (!supabase) return getSoundsByVibeCategoryFallback(vibe, limit, options)

  // Primary path: vibe_tags column populated by the backfill.
  const { data, error } = await supabase
    .from('sounds')
    .select(
      'id, slug, title, category, duration, size, format, downloads, file_url, created_at, updated_at, description, tags, compatible_models, is_active, likes',
    )
    .eq('is_active', true)
    .or(EXCLUDE_MATURE_FILTER)
    .contains('vibe_tags', [slug])
    .order('downloads', { ascending: false })
    .limit(limit)

  if (error && shouldRethrow(options.throwOnError)) {
    throw new SupabaseError(`Failed to fetch sounds for vibe "${slug}": ${error.message}`, error)
  }

  if (error || !data || data.length === 0) {
    return getSoundsByVibeCategoryFallback(vibe, limit, options)
  }
  return data as Sound[]
}

async function getSoundsByVibeCategoryFallback(
  vibe: Vibe,
  limit: number,
  options: { throwOnError?: boolean },
): Promise<Sound[]> {
  const results = await Promise.all(
    vibe.categories.map((c) => soundsApi.getSoundsByCategory(c, options)),
  )
  const seen = new Set<string>()
  const merged: Sound[] = []
  for (const list of results) {
    for (const s of list) {
      if (!seen.has(s.id)) {
        seen.add(s.id)
        merged.push(s)
      }
    }
  }
  return merged
    .sort((a, b) => (b.downloads ?? 0) - (a.downloads ?? 0))
    .slice(0, limit)
}
